import {DEFAULT_BIRD_LISTENING_SETTINGS,type BirdListeningSettings} from '../../config/birdListening.js';
import {BIRD_INFERENCE_TIMEOUT_MS,BIRD_LIVE_RESULT_AGE_MS} from './inferenceHealth';
export interface BirdScore {index:number;commonName:string;scientificName:string;confidence:number;geoscore?:number}
export interface HeardBird {id:number;name:string;scientificName:string;firstHeard:number;lastHeard:number;peak:number}
// Exact non-wildlife classes in the pinned model. Do not use guide availability
// as a species filter: valid birds can lack a guide entry after taxonomy changes.
const nonBirdSounds=new Set(['Dog','Engine','Environmental','Fireworks','Gun','Noise','Siren','Human non-vocal','Human vocal','Human whistle','Power tools']);
/** Session memory only. Fixed insertion order; predictions never become confirmed observations. */
export class BirdDetections {
 private birds=new Map<number,HeardBird>();
 private candidates=new Map<number,{first:number;last:number;count:number}>();
 private lastFrame=-1;
 constructor(private settings:Readonly<BirdListeningSettings>=DEFAULT_BIRD_LISTENING_SETTINGS){}
 accept(scores:BirdScore[],at:number,now=performance.now()):HeardBird[]{
  if(!Number.isFinite(at)||!Number.isFinite(now)||at<=this.lastFrame||now-at>=BIRD_INFERENCE_TIMEOUT_MS||at>now+100)return this.rows();
  this.lastFrame=at;
  const heardAt=now-at>BIRD_LIVE_RESULT_AGE_MS?at:now;
  for(const [id,c] of this.candidates)if(at-c.last>6000)this.candidates.delete(id);
  for(const s of scores){
   if(nonBirdSounds.has(s.scientificName))continue;
   if(!Number.isInteger(s.index)||!s.commonName?.trim()||!Number.isFinite(s.confidence)||s.confidence<0||s.confidence>1||s.geoscore!==undefined&&s.geoscore<.01)continue;
   const bird=this.birds.get(s.index);
   if(bird){if(s.confidence>=this.settings.activityThreshold){bird.lastHeard=heardAt;bird.peak=Math.max(bird.peak,s.confidence);}continue;}
   if(s.confidence<this.settings.newBirdThreshold)continue;
   const c=this.candidates.get(s.index);
   const candidate=!c||at-c.first>6000?{first:at,last:at,count:1}:at-c.last>=1000?{...c,last:at,count:c.count+1}:c;
   if(s.confidence>=this.settings.immediateThreshold||candidate.count>=this.settings.confirmations){
    if(this.birds.size<100)this.birds.set(s.index,{id:s.index,name:s.commonName,scientificName:s.scientificName,firstHeard:at,lastHeard:heardAt,peak:s.confidence});
    this.candidates.delete(s.index);
   }else this.candidates.set(s.index,candidate);
  }
  return this.rows();
 }
 pause(){this.candidates.clear();for(const bird of this.birds.values())bird.lastHeard=-Infinity;}
 rows(){return [...this.birds.values()].map(b=>({...b}));}
}
export function resampleAudio(input:Float32Array,rate:number,target=48000){
 if(rate===target)return input;
 const output=new Float32Array(Math.floor(input.length*target/rate));
 for(let i=0;i<output.length;i++){const p=i*rate/target,left=Math.floor(p),f=p-left;output[i]=input[left]*(1-f)+(input[Math.min(left+1,input.length-1)]||0)*f;}
 return output;
}
